import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBroadcastSettings, setActiveBroadcast, setDefaultTournament, getAvailableBroadcastIds, getTeams, getTournaments } from '@/lib/supabase';
import AppHeader from '@/components/AppHeader';
import { useAuth } from '@/lib/auth';

interface BroadcastMatchInfo {
  broadcastId: string;
  matchNumber: number;
  teamAId: string;
  teamBId: string;
  status: string;
}

interface TeamInfo {
  id: string;
  name: string;
  shortName: string;
  color: string;
}

export default function BroadcastControl() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [availableBroadcasts, setAvailableBroadcasts] = useState<BroadcastMatchInfo[]>([]);
  const [teams, setTeams] = useState<Map<string, TeamInfo>>(new Map());
  const [selectedBroadcastId, setSelectedBroadcastId] = useState<string>('');
  const [currentBroadcastId, setCurrentBroadcastId] = useState<string | null>(null);
  const [tournaments, setTournaments] = useState<{ id: string; name: string }[]>([]);
  const [selectedDefaultTournamentId, setSelectedDefaultTournamentId] = useState<string>('');
  const [currentDefaultTournamentId, setCurrentDefaultTournamentId] = useState<string | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [goingLive, setGoingLive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [broadcastsData, teamsData, settings, tournamentsData] = await Promise.all([
        getAvailableBroadcastIds(),
        getTeams(),
        getBroadcastSettings(),
        getTournaments(),
      ]);

      setAvailableBroadcasts(broadcastsData);
      setTournaments(tournamentsData.map(t => ({ id: t.id, name: t.name })));

      const teamMap = new Map<string, TeamInfo>();
      for (const t of teamsData) {
        teamMap.set(t.id, {
          id: t.id,
          name: t.name,
          shortName: t.short_name,
          color: t.color,
        });
      }
      setTeams(teamMap);

      if (settings?.activeBroadcastId) {
        setCurrentBroadcastId(settings.activeBroadcastId);
        setSelectedBroadcastId(settings.activeBroadcastId);
      }

      if (settings?.defaultTournamentId) {
        setCurrentDefaultTournamentId(settings.defaultTournamentId);
        setSelectedDefaultTournamentId(settings.defaultTournamentId);
      }

      setLoading(false);
    } catch (err) {
      console.error('[BroadcastControl] Error loading data:', err);
      setError('Failed to load broadcast data');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGoLive = async () => {
    if (!selectedBroadcastId) {
      setError('Please select a broadcast ID');
      return;
    }

    setGoingLive(true);
    setError(null);
    setSuccess(null);

    try {
      await setActiveBroadcast(selectedBroadcastId);
      setCurrentBroadcastId(selectedBroadcastId);
      setSuccess(`Now live: ${selectedBroadcastId}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[BroadcastControl] Error going live:', err);
      setError('Failed to update broadcast');
    } finally {
      setGoingLive(false);
    }
  };

  const handleClearBroadcast = async () => {
    setGoingLive(true);
    setError(null);
    setSuccess(null);

    try {
      await setActiveBroadcast(null);
      setCurrentBroadcastId(null);
      setSuccess('Broadcast cleared');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[BroadcastControl] Error clearing broadcast:', err);
      setError('Failed to clear broadcast');
    } finally {
      setGoingLive(false);
    }
  };

  const handleSaveDefaultTournament = async () => {
    setSavingDefault(true);
    setError(null);
    setSuccess(null);
    try {
      await setDefaultTournament(selectedDefaultTournamentId || null);
      setCurrentDefaultTournamentId(selectedDefaultTournamentId || null);
      setSuccess(selectedDefaultTournamentId ? 'Default tournament saved' : 'Default tournament cleared');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[BroadcastControl] Error saving default tournament:', err);
      setError('Failed to save default tournament');
    } finally {
      setSavingDefault(false);
    }
  };

  const getTeamDisplay = (teamId: string): string => {
    const team = teams.get(teamId);
    return team ? team.shortName : teamId.substring(0, 8);
  };

  const getTeamColor = (teamId: string): string => {
    const team = teams.get(teamId);
    return team?.color || '#888';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader title="Broadcast Control" navIcon="back" onNavigate={() => navigate('/admin')} onSignOut={signOut} />
        <div className="flex-1 flex items-center justify-center">
          <span className="w-6 h-6 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader title="Broadcast Control" navIcon="back" onNavigate={() => navigate('/admin')} onSignOut={signOut} />
      <div className="flex-1 overflow-y-auto px-4 py-6 safe-area-bottom max-w-md mx-auto w-full space-y-4">

      {/* Current Live */}
      {currentBroadcastId && (
        <div className="bg-success/10 border border-success/30 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs font-bold text-success uppercase tracking-wider">Current Live</span>
          </div>
          <div className="mt-2 text-lg font-bold text-text-primary">{currentBroadcastId}</div>
          {(() => {
            const currentMatch = availableBroadcasts.find(b => b.broadcastId === currentBroadcastId);
            if (currentMatch) {
              return (
                <div className="flex items-center gap-2 mt-1 text-sm">
                  <span className="font-semibold" style={{ color: getTeamColor(currentMatch.teamAId) }}>
                    {getTeamDisplay(currentMatch.teamAId)}
                  </span>
                  <span className="text-text-muted">vs</span>
                  <span className="font-semibold" style={{ color: getTeamColor(currentMatch.teamBId) }}>
                    {getTeamDisplay(currentMatch.teamBId)}
                  </span>
                  <span className="text-text-muted text-xs ml-2">Match #{currentMatch.matchNumber}</span>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-error/10 border border-error/30 rounded-lg p-3">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="bg-success/10 border border-success/30 rounded-lg p-3">
          <p className="text-sm text-success">{success}</p>
        </div>
      )}

      {/* Broadcast Selection */}
      <div className="space-y-2">
        <label className="text-xs font-bold text-text-muted uppercase tracking-wider">Broadcast ID</label>
        <select
          value={selectedBroadcastId}
          onChange={(e) => setSelectedBroadcastId(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-team-a"
        >
          <option value="">Select a broadcast...</option>
          {availableBroadcasts.map((b) => (
            <option key={b.broadcastId} value={b.broadcastId}>
              {b.broadcastId} - Match #{b.matchNumber} ({getTeamDisplay(b.teamAId)} vs {getTeamDisplay(b.teamBId)})
            </option>
          ))}
        </select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleGoLive}
          disabled={!selectedBroadcastId || goingLive}
          className="flex-1 py-2 px-4 rounded-lg font-bold text-sm bg-success text-white transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {goingLive ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              UPDATING...
            </span>
          ) : (
            'Go Live'
          )}
        </button>
        {currentBroadcastId && (
          <button
            onClick={handleClearBroadcast}
            disabled={goingLive}
            className="py-2 px-4 rounded-lg font-bold text-sm bg-surface border border-border text-text-secondary transition-all active:scale-[0.98] hover:bg-surface-raised disabled:opacity-50"
          >
            Clear
          </button>
        )}
      </div>

      {/* Permanent URL Info */}
      <div className="bg-surface border border-border rounded-lg p-3">
        <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Permanent Overlay URL</p>
        <p className="text-sm font-mono text-text-primary">/overlay/live</p>
        <p className="text-xs text-text-muted mt-2">
          This URL always displays whichever match is selected above. Use it as your broadcast source in Prism Live.
        </p>
      </div>

      {/* Default Tournament for /live page */}
      <div className="border-t border-border pt-4 space-y-3">
        <div className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
            <path d="M5 4h14" /><path d="M5 4v6a7 7 0 0 0 14 0V4" />
            <line x1="12" y1="16" x2="12" y2="20" />
            <line x1="9" y1="20" x2="15" y2="20" />
          </svg>
          <h3 className="text-sm font-bold text-text-primary uppercase tracking-wider">Default Tournament</h3>
        </div>
        <p className="text-xs text-text-muted">
          The tournament shown when viewers open the public <span className="font-mono">/live</span> page. Viewers can change it themselves.
        </p>
        {currentDefaultTournamentId && (
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-2">
            <p className="text-xs font-bold text-primary">
              Current: {tournaments.find(t => t.id === currentDefaultTournamentId)?.name ?? currentDefaultTournamentId}
            </p>
          </div>
        )}
        <select
          value={selectedDefaultTournamentId}
          onChange={e => setSelectedDefaultTournamentId(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary"
        >
          <option value="">— No preference (show latest) —</option>
          {tournaments.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <button
          onClick={handleSaveDefaultTournament}
          disabled={savingDefault}
          className="w-full py-2 px-4 rounded-lg font-bold text-sm bg-primary text-white transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-50"
        >
          {savingDefault ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              SAVING...
            </span>
          ) : 'Save Default'}
        </button>
      </div>
      </div>
    </div>
  );
}
